// ============================================================
//  ALTIMO Medical Center — Turnos.jsx
//  Ubicación: src/components/Turnos.jsx
//
//  Módulo de Turnos:
//   · Pestaña "Programación": grilla semanal (personal × días).
//     Jefes de área (es_jefe=1) y admin asignan turnos con clic.
//   · Pestaña "Entrega de turno": registro de entrega con notas
//     por paciente activo + recepción por el turno entrante.
//   · Pestaña "Catálogo": definición de turnos (admin/jefes).
// ============================================================

import { useState, useEffect, useCallback } from 'react';
import api from '../apiClient';

const DIAS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const S = {
  pagina: {
    background: '#f0f4f8', minHeight: '100vh',
    padding: '20px', fontFamily: 'Segoe UI, sans-serif',
  },
  card: {
    background: '#fff', borderRadius: '10px',
    border: '1px solid #dde3ea', padding: '18px', marginBottom: '16px',
  },
  cardTitulo: {
    fontSize: '12px', fontWeight: '700', color: '#1B2A5E',
    textTransform: 'uppercase', letterSpacing: '.05em',
    marginBottom: '14px', paddingBottom: '10px', borderBottom: '2px solid #e8f0fb',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  tabla: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: {
    textAlign: 'left', padding: '8px 12px', fontSize: '11px',
    color: '#6c757d', fontWeight: '600', borderBottom: '2px solid #e8f0fb', background: '#f8fafd',
  },
  td: { padding: '9px 12px', borderBottom: '1px solid #eef1f6', color: '#333' },
  input: {
    width: '100%', fontSize: '13px', padding: '8px 10px',
    border: '1px solid #c0ccda', borderRadius: '7px',
    background: '#fff', color: '#333', outline: 'none', boxSizing: 'border-box',
  },
  label: {
    display: 'block', fontSize: '11px', fontWeight: '600', color: '#6c757d',
    marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '.04em',
  },
  campo: { marginBottom: '12px' },
  btnPrimario: {
    background: '#1D9E75', color: '#fff', border: 'none',
    borderRadius: '7px', padding: '8px 18px', fontSize: '13px', fontWeight: '700', cursor: 'pointer',
  },
  btnAzul: {
    background: '#1B2A5E', color: '#fff', border: 'none',
    borderRadius: '7px', padding: '7px 14px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
  },
  btnGris: {
    background: '#e9edf2', color: '#333', border: 'none',
    borderRadius: '7px', padding: '7px 14px', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
  },
  tab: (activa) => ({
    padding: '9px 18px', border: 'none', cursor: 'pointer',
    borderRadius: '8px 8px 0 0', fontSize: '13px', fontWeight: '700',
    background: activa ? '#fff' : 'transparent',
    color: activa ? '#1B2A5E' : '#6c757d',
    borderBottom: activa ? '3px solid #1D9E75' : '3px solid transparent',
  }),
  pillTurno: (color) => ({
    display: 'inline-block', padding: '3px 8px', borderRadius: '6px',
    fontSize: '11px', fontWeight: '700', color: '#fff', background: color || '#1B2A5E',
    margin: '1px', cursor: 'pointer',
  }),
  celdaGrid: {
    padding: '4px', borderBottom: '1px solid #eef1f6', borderLeft: '1px solid #f2f5f9',
    minWidth: '95px', verticalAlign: 'top', textAlign: 'center', minHeight: '38px',
  },
  aviso: {
    padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '14px',
    background: '#fff3cd', color: '#856404', border: '1px solid #ffeeba',
  },
  badge: (bg, color) => ({
    display: 'inline-block', padding: '2px 9px', borderRadius: '99px',
    fontSize: '10px', fontWeight: '700', background: bg, color,
  }),
  textarea: {
    width: '100%', fontSize: '13px', padding: '8px 10px', minHeight: '54px',
    border: '1px solid #c0ccda', borderRadius: '7px', boxSizing: 'border-box',
    fontFamily: 'inherit', resize: 'vertical',
  },
  modalFondo: {
    position: 'fixed', inset: 0, background: 'rgba(20,30,50,.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200,
  },
  modal: {
    background: '#fff', borderRadius: '12px', padding: '22px',
    width: 'min(680px, 94vw)', maxHeight: '88vh', overflowY: 'auto',
  },
};

// ── Utilidades de fechas (semana Lun–Dom) ───────────────────
function inicioSemana(base) {
  const d = new Date(base);
  const dia = (d.getDay() + 6) % 7; // Lun=0
  d.setDate(d.getDate() - dia);
  d.setHours(0, 0, 0, 0);
  return d;
}
function fmt(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}
function sumarDias(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function hhmm(t) { return (t || '').slice(0, 5); }

export default function Turnos({ usuario }) {
  const [tab, setTab] = useState('programacion');
  const esRolMedico = ['medico', 'jefe_medico'].includes(usuario?.rol);
  const [area, setArea] = useState(esRolMedico ? 'medicos' : 'enfermeria');

  const [catalogo, setCatalogo] = useState([]);
  const [personal, setPersonal] = useState([]);
  const [asignaciones, setAsignaciones] = useState([]);
  const [semanaBase, setSemanaBase] = useState(inicioSemana(new Date()));
  const [turnoSeleccionado, setTurnoSeleccionado] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  const esAdmin = usuario?.rol === 'admin';
  const ROLES_JEFATURA = ['jefa_enfermeria', 'supervisora_turno', 'jefe_medico'];
  const esJefe = esAdmin || ROLES_JEFATURA.includes(usuario?.rol) || !!usuario?.es_jefe;

  const dias = Array.from({ length: 7 }, (_, i) => sumarDias(semanaBase, i));
  const desde = fmt(dias[0]);
  const hasta = fmt(dias[6]);

  const cargar = useCallback(async () => {
    setCargando(true);
    const [rc, rp, ra] = await Promise.all([
      api.turnos.catalogo(),
      api.turnos.personal(area),
      api.turnos.asignaciones({ desde, hasta, area }),
    ]);
    if (rc.ok) {
      setCatalogo(rc.data);
      setTurnoSeleccionado((prev) => prev || rc.data[0]?.id || null);
    }
    if (rp.ok) setPersonal(rp.data);
    if (ra.ok) setAsignaciones(ra.data);
    setCargando(false);
  }, [area, desde, hasta]);

  useEffect(() => { cargar(); }, [cargar]);

  const avisar = (texto, esError) => {
    setMensaje({ texto, esError });
    setTimeout(() => setMensaje(null), 3500);
  };

  // ── Asignar / quitar turno con clic en celda ──────────────
  const clickCelda = async (usuarioId, fecha) => {
    if (!esJefe) return;
    if (!turnoSeleccionado) { avisar('Selecciona primero un turno del catálogo.', true); return; }
    const existente = asignaciones.find(
      (a) => a.usuario_id === usuarioId && a.fecha.slice(0, 10) === fecha && a.turno_id === turnoSeleccionado
    );
    if (existente) {
      const r = await api.turnos.eliminarAsignacion(existente.id);
      if (r.ok) cargar(); else avisar(r.error, true);
    } else {
      const r = await api.turnos.asignar({
        area,
        asignaciones: [{ usuario_id: usuarioId, fecha, turno_id: turnoSeleccionado }],
      });
      if (r.ok) cargar(); else avisar(r.error, true);
    }
  };

  return (
    <div style={S.pagina}>
      <div style={{ display: 'flex', gap: '4px', marginBottom: '0' }}>
        <button style={S.tab(tab === 'programacion')} onClick={() => setTab('programacion')}>📅 Programación</button>
        <button style={S.tab(tab === 'entrega')} onClick={() => setTab('entrega')}>🔄 Entrega de turno</button>
        {esJefe && <button style={S.tab(tab === 'catalogo')} onClick={() => setTab('catalogo')}>⚙️ Catálogo</button>}
        <div style={{ flex: 1 }} />
        <select style={{ ...S.input, width: '190px', marginBottom: '6px' }} value={area} onChange={(e) => setArea(e.target.value)}>
          <option value="enfermeria">Enfermería</option>
          <option value="medicos">Médicos</option>
        </select>
      </div>

      {mensaje && (
        <div style={{ ...S.aviso, background: mensaje.esError ? '#f8d7da' : '#d4edda', color: mensaje.esError ? '#721c24' : '#155724', border: 'none' }}>
          {mensaje.texto}
        </div>
      )}

      {tab === 'programacion' && (
        <Programacion
          dias={dias} desde={desde} personal={personal} catalogo={catalogo}
          asignaciones={asignaciones} esJefe={esJefe} cargando={cargando}
          turnoSeleccionado={turnoSeleccionado} setTurnoSeleccionado={setTurnoSeleccionado}
          clickCelda={clickCelda}
          semanaAnterior={() => setSemanaBase(sumarDias(semanaBase, -7))}
          semanaSiguiente={() => setSemanaBase(sumarDias(semanaBase, 7))}
          irHoy={() => setSemanaBase(inicioSemana(new Date()))}
        />
      )}

      {tab === 'entrega' && (
        <EntregaTurno usuario={usuario} area={area} catalogo={catalogo} avisar={avisar} />
      )}

      {tab === 'catalogo' && esJefe && (
        <Catalogo catalogo={catalogo} recargar={cargar} avisar={avisar} />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  PESTAÑA 1: Programación semanal
// ════════════════════════════════════════════════════════════
function Programacion({ dias, personal, catalogo, asignaciones, esJefe, cargando,
  turnoSeleccionado, setTurnoSeleccionado, clickCelda, semanaAnterior, semanaSiguiente, irHoy }) {

  const hoy = fmt(new Date());

  return (
    <div style={S.card}>
      <div style={S.cardTitulo}>
        <span>Programación semanal · {dias[0].toLocaleDateString('es', { day: 'numeric', month: 'short' })} — {dias[6].toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
        <span style={{ display: 'flex', gap: '6px' }}>
          <button style={S.btnGris} onClick={semanaAnterior}>← Anterior</button>
          <button style={S.btnGris} onClick={irHoy}>Hoy</button>
          <button style={S.btnGris} onClick={semanaSiguiente}>Siguiente →</button>
        </span>
      </div>

      {esJefe ? (
        <div style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '12px', color: '#6c757d', fontWeight: '600' }}>Turno a asignar (clic en la celda para asignar/quitar):</span>
          {catalogo.map((t) => (
            <span key={t.id}
              onClick={() => setTurnoSeleccionado(t.id)}
              style={{ ...S.pillTurno(t.color), outline: turnoSeleccionado === t.id ? '3px solid #ffc107' : 'none' }}>
              {t.nombre} {hhmm(t.hora_inicio)}–{hhmm(t.hora_fin)}
            </span>
          ))}
        </div>
      ) : (
        <div style={S.aviso}>Vista de consulta. Solo la jefatura del área puede modificar la programación.</div>
      )}

      {cargando ? <div style={{ padding: '20px', color: '#6c757d' }}>Cargando…</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={S.tabla}>
            <thead>
              <tr>
                <th style={{ ...S.th, minWidth: '160px' }}>Personal</th>
                {dias.map((d, i) => (
                  <th key={i} style={{ ...S.th, textAlign: 'center', background: fmt(d) === hoy ? '#e8f5f0' : '#f8fafd' }}>
                    {DIAS[i]}<br /><span style={{ fontWeight: '400' }}>{d.getDate()}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {personal.map((u) => (
                <tr key={u.id}>
                  <td style={{ ...S.td, fontWeight: '600' }}>
                    {u.nombre}{(u.es_jefe || ['jefa_enfermeria','supervisora_turno','jefe_medico'].includes(u.rol)) ? <span style={{ ...S.badge('#e2d9f3', '#4a2080'), marginLeft: '6px' }}>JEFE</span> : null}
                  </td>
                  {dias.map((d, i) => {
                    const fecha = fmt(d);
                    const deldia = asignaciones.filter(
                      (a) => a.usuario_id === u.id && a.fecha.slice(0, 10) === fecha
                    );
                    return (
                      <td key={i} style={{ ...S.celdaGrid, cursor: esJefe ? 'pointer' : 'default', background: fecha === hoy ? '#f4fbf8' : undefined }}
                        onClick={() => clickCelda(u.id, fecha)}>
                        {deldia.map((a) => (
                          <span key={a.id} style={S.pillTurno(a.color)} title={`${a.turno_nombre} ${hhmm(a.hora_inicio)}–${hhmm(a.hora_fin)}${a.estacion_nombre ? ' · ' + a.estacion_nombre : ''}`}>
                            {a.turno_nombre}
                          </span>
                        ))}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {personal.length === 0 && (
                <tr><td colSpan={8} style={{ ...S.td, color: '#6c757d', textAlign: 'center' }}>No hay personal activo en esta área.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  PESTAÑA 2: Entrega / recepción de turno
// ════════════════════════════════════════════════════════════
function EntregaTurno({ usuario, area, catalogo, avisar }) {
  const [entregas, setEntregas] = useState([]);
  const [detalle, setDetalle] = useState(null);      // entrega abierta en modal (lectura/recepción)
  const [creando, setCreando] = useState(false);      // modal de nueva entrega
  const [pacientesActivos, setPacientesActivos] = useState([]);
  const [form, setForm] = useState({ turno_id: '', notas_generales: '', pacientes: {} });

  const hoy = fmt(new Date());
  const hace7 = fmt(sumarDias(new Date(), -7));

  const cargar = useCallback(async () => {
    const r = await api.turnos.entregas({ desde: hace7, hasta: hoy, area });
    if (r.ok) setEntregas(r.data);
  }, [area, hace7, hoy]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrirNueva = async () => {
    const r = await api.dashboard.pacientesActivos();
    if (r.ok) setPacientesActivos(r.data);
    setForm({ turno_id: catalogo[0]?.id || '', notas_generales: '', pacientes: {} });
    setCreando(true);
  };

  const setPacCampo = (admisionId, campo, valor) => {
    setForm((f) => ({
      ...f,
      pacientes: { ...f.pacientes, [admisionId]: { ...(f.pacientes[admisionId] || {}), [campo]: valor } },
    }));
  };

  const guardarEntrega = async () => {
    if (!form.turno_id) { avisar('Selecciona el turno que entregas.', true); return; }
    const pacientes = Object.entries(form.pacientes)
      .filter(([, v]) => (v.novedades || '').trim() || (v.pendientes || '').trim())
      .map(([admision_id, v]) => ({ admision_id: Number(admision_id), novedades: v.novedades || '', pendientes: v.pendientes || '' }));
    const r = await api.turnos.crearEntrega({
      fecha: hoy, turno_id: Number(form.turno_id), area,
      notas_generales: form.notas_generales, pacientes,
    });
    if (r.ok) { setCreando(false); avisar('Entrega de turno registrada. Queda pendiente de recepción.'); cargar(); }
    else avisar(r.error, true);
  };

  const verDetalle = async (id) => {
    const r = await api.turnos.entregaDetalle(id);
    if (r.ok) setDetalle({ ...r.data, notas_recepcion_nueva: '' });
    else avisar(r.error, true);
  };

  const recibir = async () => {
    const r = await api.turnos.recibirEntrega(detalle.id, { notas_recepcion: detalle.notas_recepcion_nueva });
    if (r.ok) { setDetalle(null); avisar('Turno recibido correctamente.'); cargar(); }
    else avisar(r.error, true);
  };

  return (
    <div style={S.card}>
      <div style={S.cardTitulo}>
        <span>Entregas de turno · últimos 7 días</span>
        <button style={S.btnPrimario} onClick={abrirNueva}>+ Entregar mi turno</button>
      </div>

      <table style={S.tabla}>
        <thead>
          <tr>
            <th style={S.th}>Fecha</th><th style={S.th}>Turno</th><th style={S.th}>Entrega</th>
            <th style={S.th}>Recibe</th><th style={S.th}>Pacientes</th><th style={S.th}>Estado</th><th style={S.th}></th>
          </tr>
        </thead>
        <tbody>
          {entregas.map((e) => (
            <tr key={e.id}>
              <td style={S.td}>{e.fecha.slice(0, 10)}</td>
              <td style={S.td}><span style={S.pillTurno(e.color)}>{e.turno_nombre}</span></td>
              <td style={S.td}>{e.entrega_nombre}</td>
              <td style={S.td}>{e.recibe_nombre || <span style={{ color: '#adb5bd' }}>—</span>}</td>
              <td style={S.td}>{e.num_pacientes}</td>
              <td style={S.td}>
                {e.estado === 'recibida'
                  ? <span style={S.badge('#d4edda', '#155724')}>RECIBIDA</span>
                  : <span style={S.badge('#fff3cd', '#856404')}>PENDIENTE</span>}
              </td>
              <td style={S.td}><button style={S.btnAzul} onClick={() => verDetalle(e.id)}>Ver / Recibir</button></td>
            </tr>
          ))}
          {entregas.length === 0 && (
            <tr><td colSpan={7} style={{ ...S.td, color: '#6c757d', textAlign: 'center' }}>Sin entregas registradas en el período.</td></tr>
          )}
        </tbody>
      </table>

      {/* ── Modal: nueva entrega ── */}
      {creando && (
        <div style={S.modalFondo} onClick={() => setCreando(false)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 14px', color: '#1B2A5E', fontSize: '16px' }}>Entregar turno · {hoy}</h3>

            <div style={S.campo}>
              <label style={S.label}>Turno que entregas</label>
              <select style={S.input} value={form.turno_id} onChange={(e) => setForm({ ...form, turno_id: e.target.value })}>
                {catalogo.map((t) => <option key={t.id} value={t.id}>{t.nombre} ({hhmm(t.hora_inicio)}–{hhmm(t.hora_fin)})</option>)}
              </select>
            </div>

            <div style={S.campo}>
              <label style={S.label}>Novedades generales del turno</label>
              <textarea style={S.textarea} value={form.notas_generales}
                onChange={(e) => setForm({ ...form, notas_generales: e.target.value })}
                placeholder="Situaciones generales, equipos, insumos, incidencias…" />
            </div>

            <div style={{ ...S.label, marginTop: '16px' }}>Notas por paciente (llena solo los que tengan novedades o pendientes)</div>
            {pacientesActivos.map((p) => (
              <div key={p.id} style={{ border: '1px solid #eef1f6', borderRadius: '8px', padding: '10px', marginBottom: '8px' }}>
                <div style={{ fontWeight: '700', fontSize: '13px', color: '#1B2A5E', marginBottom: '6px' }}>
                  {p.paciente} {p.habitacion ? `· Hab. ${p.habitacion}` : ''}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <div>
                    <label style={S.label}>Novedades</label>
                    <textarea style={{ ...S.textarea, minHeight: '44px' }}
                      value={form.pacientes[p.id]?.novedades || ''}
                      onChange={(e) => setPacCampo(p.id, 'novedades', e.target.value)} />
                  </div>
                  <div>
                    <label style={S.label}>Pendientes</label>
                    <textarea style={{ ...S.textarea, minHeight: '44px' }}
                      value={form.pacientes[p.id]?.pendientes || ''}
                      onChange={(e) => setPacCampo(p.id, 'pendientes', e.target.value)} />
                  </div>
                </div>
              </div>
            ))}
            {pacientesActivos.length === 0 && <div style={{ fontSize: '13px', color: '#6c757d' }}>No hay pacientes activos.</div>}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '14px' }}>
              <button style={S.btnGris} onClick={() => setCreando(false)}>Cancelar</button>
              <button style={S.btnPrimario} onClick={guardarEntrega}>Registrar entrega</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: detalle / recepción ── */}
      {detalle && (
        <div style={S.modalFondo} onClick={() => setDetalle(null)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 4px', color: '#1B2A5E', fontSize: '16px' }}>
              Entrega de turno · {detalle.fecha.slice(0, 10)} · {detalle.turno_nombre}
            </h3>
            <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '12px' }}>
              Entregó: <b>{detalle.entrega_nombre}</b> ({new Date(detalle.entregado_en).toLocaleString('es')})
              {detalle.estado === 'recibida' && <> · Recibió: <b>{detalle.recibe_nombre}</b></>}
            </div>

            {detalle.notas_generales && (
              <div style={{ ...S.campo }}>
                <label style={S.label}>Novedades generales</label>
                <div style={{ fontSize: '13px', background: '#f8fafd', borderRadius: '7px', padding: '10px' }}>{detalle.notas_generales}</div>
              </div>
            )}

            {detalle.pacientes?.map((p) => (
              <div key={p.id} style={{ border: '1px solid #eef1f6', borderRadius: '8px', padding: '10px', marginBottom: '8px' }}>
                <div style={{ fontWeight: '700', fontSize: '13px', color: '#1B2A5E' }}>
                  {p.paciente_nombre} {p.habitacion ? `· Hab. ${p.habitacion}` : ''}
                </div>
                {p.novedades && <div style={{ fontSize: '13px', marginTop: '4px' }}><b>Novedades:</b> {p.novedades}</div>}
                {p.pendientes && <div style={{ fontSize: '13px', marginTop: '2px', color: '#856404' }}><b>Pendientes:</b> {p.pendientes}</div>}
              </div>
            ))}

            {detalle.estado === 'recibida' ? (
              detalle.notas_recepcion && (
                <div style={S.campo}>
                  <label style={S.label}>Observaciones de recepción</label>
                  <div style={{ fontSize: '13px', background: '#f4fbf8', borderRadius: '7px', padding: '10px' }}>{detalle.notas_recepcion}</div>
                </div>
              )
            ) : (
              <>
                <div style={S.campo}>
                  <label style={S.label}>Observaciones al recibir (opcional)</label>
                  <textarea style={S.textarea} value={detalle.notas_recepcion_nueva}
                    onChange={(e) => setDetalle({ ...detalle, notas_recepcion_nueva: e.target.value })} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                  <button style={S.btnGris} onClick={() => setDetalle(null)}>Cerrar</button>
                  <button style={S.btnPrimario} onClick={recibir}>✓ Recibir turno</button>
                </div>
              </>
            )}
            {detalle.estado === 'recibida' && (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button style={S.btnGris} onClick={() => setDetalle(null)}>Cerrar</button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
//  PESTAÑA 3: Catálogo de turnos
// ════════════════════════════════════════════════════════════
function Catalogo({ catalogo, recargar, avisar }) {
  const [form, setForm] = useState({ nombre: '', hora_inicio: '07:00', hora_fin: '15:00', color: '#1B2A5E' });

  const crear = async () => {
    if (!form.nombre.trim()) { avisar('El nombre del turno es requerido.', true); return; }
    const r = await api.turnos.crearTurnoCatalogo(form);
    if (r.ok) { setForm({ nombre: '', hora_inicio: '07:00', hora_fin: '15:00', color: '#1B2A5E' }); avisar('Turno creado.'); recargar(); }
    else avisar(r.error, true);
  };

  const desactivar = async (t) => {
    const r = await api.turnos.editarTurnoCatalogo(t.id, { ...t, activo: 0 });
    if (r.ok) { avisar('Turno desactivado.'); recargar(); } else avisar(r.error, true);
  };

  return (
    <div style={S.card}>
      <div style={S.cardTitulo}><span>Catálogo de turnos</span></div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: '8px', alignItems: 'end', marginBottom: '16px' }}>
        <div><label style={S.label}>Nombre</label>
          <input style={S.input} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Ej. Fin de semana" /></div>
        <div><label style={S.label}>Inicio</label>
          <input style={S.input} type="time" value={form.hora_inicio} onChange={(e) => setForm({ ...form, hora_inicio: e.target.value })} /></div>
        <div><label style={S.label}>Fin</label>
          <input style={S.input} type="time" value={form.hora_fin} onChange={(e) => setForm({ ...form, hora_fin: e.target.value })} /></div>
        <div><label style={S.label}>Color</label>
          <input style={{ ...S.input, padding: '4px', height: '36px' }} type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} /></div>
        <button style={S.btnPrimario} onClick={crear}>+ Agregar</button>
      </div>

      <table style={S.tabla}>
        <thead><tr><th style={S.th}>Turno</th><th style={S.th}>Horario</th><th style={S.th}></th></tr></thead>
        <tbody>
          {catalogo.map((t) => (
            <tr key={t.id}>
              <td style={S.td}><span style={S.pillTurno(t.color)}>{t.nombre}</span></td>
              <td style={S.td}>{hhmm(t.hora_inicio)} – {hhmm(t.hora_fin)}</td>
              <td style={S.td}><button style={S.btnGris} onClick={() => desactivar(t)}>Desactivar</button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
